
import {
  computed,
  defineComponent,
  onMounted,
  ref,
  watchEffect,
  watch,
} from "vue";
import Images from "@/components/wizard/steps/Images.vue";
import UploadPitchDeck from "@/components/wizard/steps/UploadPitchDeck.vue";
import BusinessDescription from "@/components/wizard/steps/BusinessDescription.vue";
import BusinessDetails from "@/components/wizard/steps/BusinessDetails.vue";
import AdvancedAssurance from "@/components/wizard/steps/AdvancedAssurance.vue";
import IndustrySectorsBusinesses from "@/components/wizard/steps/IndustrySectorsBusinesses.vue";
import TeamMembers from "@/components/wizard/steps/TeamMembers.vue";
import FundingRound from "@/components/wizard/steps/FundingRound.vue";
import FindExperts from "@/views/crafted/pages/FindExperts.vue";
import { StepperComponent } from "@/assets/ts/components";
import * as Yup from "yup";
import { useForm } from "vee-validate";
import { setCurrentPageBreadcrumbs } from "@/core/helpers/breadcrumb";
import { useStore } from "vuex";
import { useRouter } from "vue-router";
import { Actions } from "@/store/enums/StoreEnums";
import objectPath from "object-path";

interface IStep1 {
  name: string;
  jobTitle: string;
  telephone: string;
  linkedInProfileUrl: string;
}

interface IStep2 {
  introduction: string;
  bio: string;
}

interface IStep3 {
  linkToExternalDocuments: string;
  documents: unknown;
}

interface IStep4 {
  expertIndustrySectors: Array<number | string>;
  expertExpertise: Array<number | string>;
}

interface CreateAccount extends IStep1, IStep2, IStep3, IStep4 {}

export default defineComponent({
  name: "Investment-Opportunity",
  components: {
    FundingRound,
    Images,
    UploadPitchDeck,
    BusinessDescription,
    BusinessDetails,
    AdvancedAssurance,
    TeamMembers,
    IndustrySectorsBusinesses,
    FindExperts,
  },
  setup() {
    const isSelected = ref<boolean>(false);
    const showSuccess = ref<boolean>(false);
    const opensAtAdded = ref<boolean>(false);
    const closesAtAdded = ref<boolean>(false);
    const areDatesValid = ref<boolean>(false);
    const loading = ref<boolean>(false);
    const touched = ref<boolean>(false);
    const touchedAdvancedAssurance = ref<boolean>(false);
    const teamMembersLength = ref<number>(0);
    const industrySectorsLength = ref<number>(0);
    let roleId = 0;
    const roles = computed(() => {
      return store.getters.getRolesData;
    });
    const fileSizeError = ref<boolean>(false);
    const store = useStore();
    const businessDraft = computed(() => store.getters.businessDraftGetter);
    watch(businessDraft, (value) => {
      formDataTemp.value = { ...value };
    });
    const user = computed(() => store.getters.getUser);
    const formDataTemp = ref<Record<string, unknown>>({});
    const _stepperObj = ref<StepperComponent | null>(null);
    const verticalWizardRef = ref<HTMLElement | null>(null);
    const currentStepIndex = ref(0);
    const formData = ref<CreateAccount>({
      name: "",
      jobTitle: "",
      telephone: "",
      linkedInProfileUrl: "",
      introduction: "",
      bio: "",
      linkToExternalDocuments: "",
      documents: null,
      expertIndustrySectors: [],
      expertExpertise: [],
    });
    const touchedIndustrySector = ref<boolean>(false);
    const handleTouch = () => {
      if (_stepperObj.value?.currentStepIndex == 5)
        touchedAdvancedAssurance.value = true;
      if (_stepperObj.value?.currentStepIndex == 6) touched.value = true;
      if (_stepperObj.value?.currentStepIndex == 7)
        touchedIndustrySector.value = true;
    };
    onMounted(async () => {
      _stepperObj.value = StepperComponent.createInsance(
        verticalWizardRef.value as HTMLElement
      );
      try {
        await store.dispatch(Actions.GET_BUSINESS_DRAFT);
        if (!roles.value.length) {
          const response = await store.dispatch(Actions.GET_ROLES);
          if (response !== true) throw new Error();
          roleId = objectPath.get(
            roles.value.find((role) => role.name.toLowerCase() === "founder"),
            "id",
            0
          );
        } else
          roleId = objectPath.get(
            roles.value.find((role) => role.name.toLowerCase() === "founder"),
            "id",
            0
          );
      } catch (err) {
        console.log("error in fetching roles");
      }
      setCurrentPageBreadcrumbs("Create Investment Opportunity", [
        "Businesses",
      ]);
    });
    const createAccountSchema = computed(() => {
      let tempObj = {};
      for (let i = 0; i < +teamMembersLength.value; i++) {
        tempObj[`team-member-name-${i}`] = Yup.string()
          .required()
          .label("Name");
        tempObj[`team-member-linkedInProfileUrl-${i}`] = Yup.string()
          .required()
          .label("LinkedIn Profile");
        tempObj[`team-member-jobTitle-${i}`] = Yup.string()
          .required()
          .label("Job Title");
        tempObj[`team-member-introduction-${i}`] = Yup.string()
          .required()
          .label("Introduction");
      }
      return [
        {},
        Yup.object({
          pitchDeckDocument: Yup.mixed()
            .required()
            .label("Pitch Deck Document"),
        }),
        Yup.object({
          name: Yup.string().required().label("Name"),
          summary: Yup.string().required().label("Summary"),
          overview: Yup.string().required().label("Overview"),
          defensibleUsp: Yup.string().required().label("Defensible USP"),
        }),
        Yup.object({
          telephone: Yup.string()
            .required()
            .min(12, "Telephone must be 11 characters long.")
            .label("Telephone"),
          website: Yup.string()
            .matches(
              /((https?):\/\/)?(www.)?[a-z0-9]+(\.[a-z]{2,}){1,3}(#?\/?[a-zA-Z0-9#]+)*\/?(\?[a-zA-Z0-9-_]+=[a-zA-Z0-9-%]+&?)?$/,
              "Enter correct url!"
            )
            .required()
            .label("Website"),
          currencyCode: Yup.string().required().label("Currency"),
          geoFocusCountryCode: Yup.string().required().label("Geo Focus"),
        }),
        {},
        tempObj,
        {},
        Yup.object({
          fundingRoundName: Yup.string().required().label("Name"),
          fundingRoundInvestmentRequired: Yup.string()
            .required()
            .label("Investment Required"),
          fundingRoundPreMoneyValuation: Yup.string()
            .required()
            .label("Pre-Money Valuation"),
          fundingRoundMinimumInvestment: Yup.string()
            .required()
            .label("Minimum Investment"),
        }),
      ];
    });
    const currentSchema = ref<Record<string, unknown>>({});
    watchEffect(() => {
      currentSchema.value = createAccountSchema.value[currentStepIndex.value];
    });

    const { resetForm, handleSubmit } = useForm<
      IStep1 | IStep2 | IStep3 | IStep4
    >({
      validationSchema: currentSchema,
    });

    const totalSteps = computed(() => {
      if (!_stepperObj.value) {
        return;
      }

      return _stepperObj.value.totatStepsNumber;
    });

    resetForm({
      values: {
        ...formData.value,
      },
    });

    const handleStep = handleSubmit(async (values) => {
      if (!isSelected.value && _stepperObj.value?.currentStepIndex == 5) {
        return;
      }
      if (!teamMembersLength.value && _stepperObj.value?.currentStepIndex == 6)
        return;
      if (
        !industrySectorsLength.value &&
        _stepperObj.value?.currentStepIndex == 7
      )
        return;
      if (fileSizeError.value) return;
      if (areDatesValid.value && _stepperObj.value?.currentStepIndex == 8)
        return;
      if (
        (!opensAtAdded.value || !closesAtAdded.value) &&
        _stepperObj.value?.currentStepIndex == 8
      )
        return;
      formData.value = {
        ...formData.value,
        ...values,
      };
      await store.dispatch(Actions.CREATE_BUSINESS_DRAFT, {
        business: JSON.stringify(formDataTemp.value),
      });
      store.dispatch(Actions.LOG_TO_SLACK, JSON.stringify(formDataTemp.value))
      currentStepIndex.value++;

      if (!_stepperObj.value) {
        return;
      }

      _stepperObj.value.goNext();
    });

    const previousStep = () => {
      if (!_stepperObj.value) {
        return;
      }

      currentStepIndex.value--;

      _stepperObj.value.goPrev();
    };
    const setFormDataTemp = (data) => {
      formDataTemp.value = { ...data };
    };
    const formSubmit = async () => {
      if ((user.value?.founderRequisiteExpert?.length ?? 0) < 3) {
        store.commit("setAlert", {
          message: "You must Connect with three experts to complete this step",
          subMessage: "",
          variant: "danger",
          duration: 4000,
          show: true,
        });
        return;
      }
      loading.value = true;
      try {
        if (
          !objectPath.get(user.value, "founderBusiness.length") &&
          !user.value?.userRoleRequests?.some((request) => {
            return request?.role?.name === "founder";
          })
        ) {
          const attachRoleResponse = await store.dispatch(Actions.ATTACH_ROLE, {
            id: roleId,
          });
          if (attachRoleResponse !== true) {
            loading.value = false;
            store.commit("setAlert", {
              message: "Error",
              subMessage: "`Error in Attaching Role`",
              variant: "danger",
              duration: 4000,
              show: true,
            });
            return;
          }
        }
        const response = await store.dispatch(
          Actions.CREATE_FOUNDER_BUSINESS,
          formDataTemp.value
        );
      store.dispatch(Actions.LOG_TO_SLACK, JSON.stringify({formSubmitBusiness : formDataTemp.value}))
        if (response !== true) throw new Error();
        const deleteResponse = await store.dispatch(
          Actions.DELETE_BUSINESS_DRAFT
        );
        if (deleteResponse !== true) throw new Error("Error in deleting draft");
        else await store.dispatch(Actions.GET_BUSINESS_DRAFT);
        loading.value = false;
        store.commit("setAlert", {
          message: "Success",
          subMessage: `Investment Opportunity ${"Created"}`,
          variant: "primary",
          duration: 4000,
          show: true,
        });
        showSuccess.value = true;
        store.dispatch(Actions.AUTH_USER);
      } catch (err) {
        store.dispatch(Actions.AUTH_USER);
        loading.value = false;
        store.commit("setAlert", {
          message: "Error",
          subMessage: "`Investment Opportunity Creation Unsuccessful`",
          variant: "danger",
          duration: 4000,
          show: true,
        });
      }
    };

    return {
      touchedAdvancedAssurance,
      isSelected,
      showSuccess,
      touchedIndustrySector,
      industrySectorsLength,
      opensAtAdded,
      closesAtAdded,
      areDatesValid,
      loading,
      createAccountSchema,
      formDataTemp,
      verticalWizardRef,
      previousStep,
      handleStep,
      formSubmit,
      totalSteps,
      currentStepIndex,
      fileSizeError,
      teamMembersLength,
      touched,
      handleTouch,
      _stepperObj,
      setFormDataTemp,
    };
  },
  beforeRouteEnter(to, from, next) {
    next((vm) => {
      if (from.name === "expert-global") {
        setTimeout(() => {
          (vm as any)._stepperObj.goto(9);
        }, 1);
      }
    });
  },
});
